import axios from 'axios';

// Configuración base de Axios
// Usar proxy de Apache para data.gashpe.app: "/api" por defecto
// En desarrollo local, usar URL directa sin prefijo /api/
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  // Algunas respuestas de pivot pueden ser muy grandes
  timeout: 1800000  // 30 minutos para archivos grandes
});

// Interceptor para agregar token de autenticación
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejo de respuestas y seguridad
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // ⚠️ SOLUCIÓN: Solo limpiar el token, NO forzar reload
      // El componente App manejará el estado de autenticación correctamente
      const currentPath = window.location.pathname;
      
      // Solo limpiar localStorage si NO estamos en proceso de verificación inicial
      // (evitar limpiar durante el refresh de página)
      if (!currentPath.includes('/login')) {
        console.warn('🔒 SEGURIDAD: Token expirado o inválido.');
        localStorage.removeItem('authToken');
        localStorage.removeItem('gaero-user-data');
        
        // NO forzar reload - dejar que React maneje el estado
        // window.location.reload(); // ❌ REMOVIDO: Causa pérdida de sesión
      }
    }
    return Promise.reject(error);
  }
);

// Funciones de autenticación
export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },
  
  // ⚠️ CRÍTICO: Verificar que el token actual sea válido
  verifyToken: async () => {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      // Si el token es inválido, el interceptor ya limpiará localStorage
      throw error;
    }
  },
  
  logout: async () => {
    const response = await api.post('/auth/logout');
    localStorage.removeItem('authToken');
    localStorage.removeItem('gaero-user-data'); // ⚠️ SOLUCIÓN: Limpiar también datos del usuario
    return response.data;
  }
};

// Funciones de Newton Forecasting
export const newtonAPI = {
  forecast: async (requestData) => {
    const response = await api.post('/newton/forecast', requestData);
    return response.data;
  },
  
  forecastRun: async (formData, onProgress) => {
    try {
      // Simular progreso para demostración
      if (onProgress) {
        setTimeout(() => onProgress({ progress: 25, message: 'Cargando datos...' }), 1000);
        setTimeout(() => onProgress({ progress: 50, message: 'Procesando pronósticos...' }), 3000);
        setTimeout(() => onProgress({ progress: 75, message: 'Generando métricas...' }), 5000);
        setTimeout(() => onProgress({ progress: 90, message: 'Finalizando...' }), 7000);
      }
      
      const response = await api.post('/newton/forecast:run', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error en forecastRun:', error);
      throw error;
    }
  },
  
  forecastEnqueue: async (formData) => {
    try {
      const response = await api.post('/newton/forecast:enqueue', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error en forecastEnqueue:', error);
      throw error;
    }
  },

  getResults: async (forecastId) => {
    const response = await api.get(`/newton/results/${forecastId}`);
    return response.data;
  },
  
  downloadResults: async (token) => {
    const response = await api.get(`/newton/download/${token}`, {
      responseType: 'blob',
    });
    return response.data;
  }
};

// Funciones de Amazon Treat
export const amazonAPI = {
  process: async (requestData) => {
    const response = await api.post('/amazon/process', requestData);
    return response.data;
  }
};

// Funciones de Dashboard - Paridad 1:1 con dashboard_ui.py
export const dashboardAPI = {
  // ⚠️ SEGURIDAD: Usar solo endpoints con autenticación
  datasources: async () => {
    const response = await api.get('/dashboard/datasources');
    return response.data;
  },

  loadFromDatabase: async (marketplace) => {
    const formData = new FormData();
    formData.append('marketplace', marketplace);
    
    // ⚠️ SEGURIDAD: Usar solo endpoint con autenticación
    const response = await api.post('/dashboard/load-from-database', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  providerAnalysis: async (marketplace, proveedor = null) => {
    const params = { marketplace };
    if (proveedor && proveedor !== 'Todos los proveedores') {
      params.proveedor = proveedor;
    }
    
    // ⚠️ SEGURIDAD: Usar solo endpoint con autenticación
    const response = await api.get(`/dashboard/provider-analysis/${marketplace}`, { params });
    return response.data;
  },

  // ⚠️ SEGURIDAD CRÍTICA: Eliminado bypass automático a endpoints públicos
  providerPivotTable: async (marketplace, proveedor = null, forceRefresh = false) => {
    const params = {};
    if (proveedor && proveedor !== 'Todos los proveedores') {
      params.proveedor = proveedor;
    }
    
    // Cache busting: agregar timestamp cuando se fuerza refresh
    if (forceRefresh) {
      params._t = Date.now();
    }
    
    // Solo usar endpoint con autenticación - NO BYPASS
    const response = await api.get(`/dashboard/provider-analysis/${marketplace}/pivot-table`, { params });
    return response.data;
  },

  providerTemporalAnalysis: async (marketplace, proveedor = null) => {
    const params = {};
    if (proveedor && proveedor !== 'Todos los proveedores') {
      params.proveedor = proveedor;
    }
    
    const response = await api.get(`/dashboard/provider-analysis/${marketplace}/temporal-analysis`, { params });
    return response.data;
  },

  providerSkusAnalysis: async (marketplace, proveedor = null) => {
    const params = {};
    if (proveedor && proveedor !== 'Todos los proveedores') {
      params.proveedor = proveedor;
    }
    
    const response = await api.get(`/dashboard/provider-analysis/${marketplace}/skus-by-provider`, { params });
    return response.data;
  },

  // ⚠️ SEGURIDAD: Usar solo endpoint con autenticación
  marketplaces: async () => {
    const response = await api.get('/dashboard/marketplaces');
    return response.data;
  },

  upload: async (file, marketplace) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('marketplace', marketplace);

    const response = await api.post('/dashboard/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  options: async (datasetId, marketplace) => {
    const response = await api.get('/dashboard/options', { 
      params: { dataset_id: datasetId, marketplace } 
    });
    return response.data;
  },

  apply: async (formData) => {
    const response = await api.post('/dashboard/apply', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // Análisis Proveedor
  proveedores: {
    equivalencias: async (marketplace) => {
      const response = await api.get('/dashboard/proveedores/equivalencias', {
        params: { marketplace }
      });
      return response.data;
    },

    pivot: async (marketplace) => {
      const response = await api.get('/dashboard/proveedores/pivot', {
        params: { marketplace }
      });
      return response.data;
    },

    // ⚠️ SEGURIDAD CRÍTICA: Eliminado acceso público - requiere autenticación
    pivotSimplificada: async (requestData) => {
      const response = await api.post('/dashboard/proveedores/pivot-simplificada', requestData);
      return response.data;
    },

    saveBuyIntelligence: async (rows) => {
      const response = await api.post('/dashboard/proveedores/buy-intelligence/save', { rows });
      return response.data;
    },

    syncGashpe: async (marketplace) => {
      const formData = new FormData();
      formData.append('marketplace', marketplace);

      const response = await api.post('/dashboard/proveedores/sync-gashpe/brands', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },

    // NUEVAS FUNCIONES PARA ADMINISTRACIÓN DE DATOS
    deleteBuyIntelligence: async () => {
      const response = await api.delete('/dashboard/proveedores/buy-intelligence/delete-all');
      return response.data;
    },

    migrateAllData: async () => {
      const response = await api.post('/dashboard/proveedores/migrate-all-data');
      return response.data;
    },
  },

  // Análisis Financiero
  financial: {
    analysis: async (marketplace, forceRefresh = false) => {
      const response = await api.get('/dashboard/financial/analysis', {
        params: { marketplace, force_refresh: forceRefresh }
      });
      return response.data;
    },

    save: async (data) => {
      const response = await api.post('/dashboard/financial/save', data);
      return response.data;
    },
  },

  // Pronóstico Global
  forecast: {
    global: async (marketplace, startDate = null, endDate = null) => {
      const params = { marketplace };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await api.get('/dashboard/forecast/global', { params });
      return response.data;
    },
  },

  // Análisis SKU
  sku: {
    search: async (query, marketplace, limit = 50) => {
      const response = await api.get('/dashboard/sku/search', {
        params: { query, marketplace, limit }
      });
      return response.data;
    },

    series: async (sku, marketplace) => {
      const response = await api.get('/dashboard/sku/series', {
        params: { sku, marketplace }
      });
      return response.data;
    },
  },

  // Métricas
  metrics: {
    comparison: async (marketplace, period = 'monthly') => {
      const response = await api.get('/dashboard/metrics/comparison', {
        params: { marketplace, period }
      });
      return response.data;
    },
  },

  // Exportaciones
  export: {
    csv: async (dataType, marketplace) => {
      const response = await api.get('/dashboard/export/csv', {
        params: { data_type: dataType, marketplace },
        responseType: 'blob'
      });
      return response.data;
    },
  },

  // Cargar a Base de Datos - Réplica exacta de Streamlit
  uploadToDatabase: {
    // Paso 1: Validar archivo CSV
    validateCSV: async (file, marketplace) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('marketplace', marketplace);

      const response = await api.post('/dashboard/upload-to-database', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000 // 2 minutos para archivos grandes
      });
      return response.data;
    },

    // Paso 2: Confirmar y ejecutar carga a BD
    confirmUpload: async (uploadId, marketplace) => {
      const formData = new FormData();
      formData.append('upload_id', uploadId);
      formData.append('marketplace', marketplace);

      const response = await api.post('/dashboard/confirm-upload-to-database', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 1800000 // 30 minutos para cargas grandes
      });
      return response.data;
    },
  },
};

// Funciones de Concatenador
export const concatenatorAPI = {
  // Obtener marketplaces disponibles
  getAvailableMarketplaces: async () => {
    const response = await api.get('/concatenator/marketplaces');
    return response.data;
  },

  // Cargar concatenado base desde BD
  loadBaseData: async (marketplace) => {
    const response = await api.post('/concatenator/load-base', { marketplace });
    return response.data;
  },

  // Iniciar procesamiento con progreso en tiempo real
  startProcessing: async (data) => {
    const response = await api.post('/concatenator/process/start', data);
    return response.data;
  },

  // Aplicar limpieza de datos
  applyCleaning: async (data) => {
    const response = await api.post('/concatenator/cleaning/apply', data);
    return response.data;
  },

  // Guardar en base de datos
  saveToDatabase: async (data) => {
    const response = await api.post('/concatenator/save', data);
    return response.data;
  },

  // Exportar datos en diferentes formatos
  exportData: async (data, format) => {
    const response = await api.post(`/concatenator/export/${format}`, data, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Legacy: Procesamiento simple de archivos
  processFiles: async (formData) => {
    const response = await api.post('/concatenator/process', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Exportar archivo completo (no solo muestra)
  exportFull: async (formData) => {
    const response = await api.post('/concatenator/export-full', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      responseType: 'blob',
      timeout: 3600000, // 60 minutos para archivos muy grandes (800+ MB)
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        console.log(`Upload progress: ${percentCompleted}%`);
      },
      onDownloadProgress: (progressEvent) => {
        const percentCompleted = progressEvent.total 
          ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
          : 0;
        console.log(`Download progress: ${percentCompleted}%`, progressEvent.loaded, 'bytes');
      }
    });
    return response.data;
  }
};

// Funciones de Automatizaciones
export const automationsAPI = {
  getAll: async () => {
    const response = await api.get('/automations');
    return response.data;
  },
  
  execute: async (automationId) => {
    const response = await api.post(`/automations/${automationId}/execute`);
    return response.data;
  },
  
  create: async (automationData) => {
    const response = await api.post('/automations', automationData);
    return response.data;
  },
  
  getLogs: async () => {
    const response = await api.get('/automations/logs');
    return response.data;
  }
};

// Funciones de Movimientos
export const movimientosAPI = {
  getAll: async (filters = {}) => {
    const response = await api.post('/movimientos/search', filters);
    return response.data;
  },
  
  getSummary: async () => {
    const response = await api.get('/movimientos/summary');
    return response.data;
  }
};

// Funciones de Datos Crudos
export const datosCrudosAPI = {
  // Obtener lista de marketplaces disponibles
  getMarketplaces: async () => {
    const response = await api.get('/datos-crudos/marketplaces');
    return response.data;
  },

  // Obtener información de versiones para un marketplace
  getMarketplaceInfo: async (marketplace) => {
    const response = await api.get(`/datos-crudos/marketplace/${marketplace}/info`);
    return response.data;
  },

  // Subir archivo y obtener validación inicial
  uploadFile: async (file, marketplace, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('marketplace', marketplace);

    const response = await api.post('/datos-crudos/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      }
    });
    return response.data;
  },

  // Analizar y filtrar columnas
  analyzeColumns: async (sessionId, marketplace, autoClean = false, selectedColumns = null) => {
    const response = await api.post('/datos-crudos/analyze-columns', {
      session_id: sessionId,
      marketplace: marketplace,
      auto_clean: autoClean,
      selected_columns: selectedColumns
    });
    return response.data;
  },

  // Guardar dataset en base de datos
  saveDataset: async (sessionId, marketplace, fecha, confirmed = true) => {
    const response = await api.post('/datos-crudos/save', {
      session_id: sessionId,
      marketplace: marketplace,
      fecha: fecha,
      confirmed: confirmed
    });
    return response.data;
  },

  // Limpiar metadatos huérfanos
  cleanupOrphans: async () => {
    const response = await api.delete('/datos-crudos/cleanup-orphans');
    return response.data;
  },

  // Obtener datos actuales de un marketplace
  getCurrentData: async (marketplace, limit = 100) => {
    const response = await api.get(`/datos-crudos/marketplace/${marketplace}/current`, {
      params: { limit }
    });
    return response.data;
  },

  // Eliminar sesión temporal
  deleteSession: async (sessionId) => {
    const response = await api.delete(`/datos-crudos/session/${sessionId}`);
    return response.data;
  }
};

// Funciones de Proveedores
export const proveedoresAPI = {
  getAll: async () => {
    const response = await api.get('/proveedores');
    return response.data;
  },
  
  getAnalysis: async (proveedorId) => {
    const response = await api.get(`/proveedores/${proveedorId}/analysis`);
    return response.data;
  },
  
  generateAnalysis: async () => {
    const response = await api.post('/proveedores/generate-analysis');
    return response.data;
  },
  
  getReports: async () => {
    const response = await api.get('/proveedores/reports');
    return response.data;
  }
};

// Funciones de Calidad de Datos
export const calidadDatosAPI = {
  getOverallReport: async () => {
    const response = await api.get('/calidad-datos/report');
    return response.data;
  },
  
  getDatasets: async () => {
    const response = await api.get('/calidad-datos/datasets');
    return response.data;
  },
  
  analyzeDataset: async (datasetId) => {
    const response = await api.post(`/calidad-datos/analyze/${datasetId}`);
    return response.data;
  },
  
  getIssues: async () => {
    const response = await api.get('/calidad-datos/issues');
    return response.data;
  },
  
  generateReport: async (reportType) => {
    const response = await api.post('/calidad-datos/generate-report', { type: reportType });
    return response.data;
  }
};

// Función de verificación de salud
export const healthAPI = {
  check: async () => {
    const response = await api.get('/health');
    return response.data;
  }
};

// Función auxiliar para manejo de errores
export const handleAPIError = (error) => {
  console.error('API Error:', error);
  
  if (error.response) {
    // Error de respuesta del servidor
    return {
      success: false,
      message: error.response.data?.detail || error.response.data?.message || 'Error del servidor',
      status: error.response.status
    };
  } else if (error.request) {
    // Error de red
    return {
      success: false,
      message: 'Error de conexión con el servidor',
      status: 0
    };
  } else {
    // Error de configuración
    return {
      success: false,
      message: 'Error interno de la aplicación',
      status: 0
    };
  }
};

// Funciones de Protocolos de Newton
export const newtonProtocolsAPI = {
  // Fuentes de datos
  dataSources: {
    getAll: async () => {
      const response = await api.get('/api/newton-protocols/data-sources');
      return response.data;
    },
    
    getById: async (sourceId) => {
      const response = await api.get(`/api/newton-protocols/data-sources/${sourceId}`);
      return response.data;
    },
    
    create: async (sourceData) => {
      const response = await api.post('/api/newton-protocols/data-sources', sourceData);
      return response.data;
    },
    
    update: async (sourceId, sourceData) => {
      const response = await api.put(`/api/newton-protocols/data-sources/${sourceId}`, sourceData);
      return response.data;
    },
    
    delete: async (sourceId) => {
      const response = await api.delete(`/api/newton-protocols/data-sources/${sourceId}`);
      return response.data;
    },
    
    testConnection: async (testData) => {
      const response = await api.post('/api/newton-protocols/data-sources/test-connection', testData);
      return response.data;
    },
    
    getData: async (sourceId, limit = 1000) => {
      const response = await api.get(`/api/newton-protocols/data-sources/${sourceId}/data`, {
        params: { limit }
      });
      return response.data;
    },
    
    // 🔍 Auto-descubrimiento inteligente
    discover: async () => {
      const response = await api.post('/api/newton-protocols/data-sources/discover');
      return response.data;
    },
    
    // 🔄 Sincronización automática
    sync: async () => {
      const response = await api.post('/api/newton-protocols/data-sources/sync');
      return response.data;
    },
    
    // 📤 Upload de archivos CSV/Excel
    uploadFile: async (formData) => {
      const response = await api.post('/api/newton-protocols/data-sources/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    },
    
    // 📊 Estadísticas avanzadas de fuente
    getStats: async (sourceId) => {
      const response = await api.get(`/api/newton-protocols/data-sources/${sourceId}/stats`);
      return response.data;
    }
  },
  
  // Protocolos
  protocols: {
    getAll: async () => {
      const response = await api.get('/api/newton-protocols/protocols');
      return response.data;
    },
    
    getById: async (protocolId) => {
      const response = await api.get(`/api/newton-protocols/protocols/${protocolId}`);
      return response.data;
    },
    
    create: async (protocolData) => {
      const response = await api.post('/api/newton-protocols/protocols', protocolData);
      return response.data;
    },
    
    update: async (protocolId, protocolData) => {
      const response = await api.put(`/api/newton-protocols/protocols/${protocolId}`, protocolData);
      return response.data;
    },
    
    delete: async (protocolId) => {
      const response = await api.delete(`/api/newton-protocols/protocols/${protocolId}`);
      return response.data;
    },
    
    // 🔗 Compartir protocolo
    share: async (protocolId, sharedWithUserId, permission = 'view') => {
      const response = await api.post(`/api/newton-protocols/protocols/${protocolId}/share`, {
        shared_with_user_id: sharedWithUserId,
        permission
      });
      return response.data;
    },
    
    // 👥 Obtener protocolos compartidos conmigo
    getSharedWithMe: async () => {
      const response = await api.get('/api/newton-protocols/protocols/shared-with-me');
      return response.data;
    },
    
    // 📋 Duplicar protocolo
    duplicate: async (protocolId) => {
      const response = await api.post(`/api/newton-protocols/protocols/${protocolId}/duplicate`);
      return response.data;
    },
    
    // 🎨 Obtener plantillas
    getTemplates: async () => {
      const response = await api.get('/api/newton-protocols/templates');
      return response.data;
    },
    
    // 🕐 Obtener versiones
    getVersions: async (protocolId) => {
      const response = await api.get(`/api/newton-protocols/protocols/${protocolId}/versions`);
      return response.data;
    },
    
    // ⏮️ Restaurar versión
    restoreVersion: async (protocolId, versionId) => {
      const response = await api.post(`/api/newton-protocols/protocols/${protocolId}/restore/${versionId}`);
      return response.data;
    }
  },
  
  // Expresiones
  expressions: {
    // ✔️ Validar expresión
    validate: async (expression, dataSourceId) => {
      const response = await api.post('/api/newton-protocols/expressions/validate', {
        expression,
        data_source_id: dataSourceId
      });
      return response.data;
    },
    
    // 🧪 Probar expresión
    test: async (expression, dataSourceId, limit = 100) => {
      const response = await api.post('/api/newton-protocols/expressions/test', {
        expression,
        data_source_id: dataSourceId,
        limit
      });
      return response.data;
    }
  },
  
  // Ejecución de Protocolos
  execution: {
    // ▶️ Ejecutar protocolo completo
    execute: async (protocolId, filters = {}, limit = 1000) => {
      const response = await api.post(`/api/newton-protocols/protocols/${protocolId}/execute`, {
        filters,
        limit
      });
      return response.data;
    },
    
    // 🧪 Test de protocolo
    test: async (protocolId, sampleSize = 100) => {
      const response = await api.post(`/api/newton-protocols/protocols/${protocolId}/test`, {
        sample_size: sampleSize
      });
      return response.data;
    }
  },
  
  // Modelo de Datos
  dataModel: {
    // ➕ Crear modelo de datos
    create: async (protocolId, name = "Modelo Principal", description = "") => {
      const response = await api.post(`/api/newton-protocols/protocols/${protocolId}/data-model`, {
        name,
        description
      });
      return response.data;
    },
    
    // 📖 Obtener modelo de datos
    get: async (protocolId) => {
      const response = await api.get(`/api/newton-protocols/protocols/${protocolId}/data-model`);
      return response.data;
    }
  }
};

// Funciones de Análisis Financiero
export const financialAPI = {
  /**
   * Cargar/generar datos financieros
   */
  loadData: async (marketplace, forceRefresh = false) => {
    const formData = new FormData();
    formData.append('marketplace', marketplace);
    formData.append('force_refresh', forceRefresh.toString());
    
    const response = await api.post('/dashboard/financial/load-data-public', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  },

  /**
   * Obtener dashboard principal con métricas
   */
  getDashboard: async (marketplace, cacheKey, horizonteTemporal = 'Mensual', tipoVolumen = 'Predicciones') => {
    const response = await api.get('/dashboard/financial/dashboard-public', {
      params: {
        marketplace,
        cache_key: cacheKey,
        horizonte_temporal: horizonteTemporal,
        tipo_volumen: tipoVolumen
      }
    });
    
    return response.data;
  },

  /**
   * Análisis por dimensión
   */
  getDimensionAnalysis: async (marketplace, cacheKey, dimension = 'proveedor', limit = 10) => {
    const response = await api.get('/dashboard/financial/dimension-analysis-public', {
      params: { marketplace, cache_key: cacheKey, dimension, limit }
    });
    
    return response.data;
  },

  /**
   * Top utilidad charts
   */
  getTopUtilityCharts: async (marketplace, cacheKey, dimension = 'proveedor') => {
    const response = await api.get('/dashboard/financial/top-utility-public', {
      params: { marketplace, cache_key: cacheKey, dimension }
    });
    
    return response.data;
  },

  /**
   * Distribución y KDE
   */
  getDistributionCharts: async (marketplace, cacheKey, nbins = 30) => {
    const response = await api.get('/dashboard/financial/distribution-public', {
      params: { marketplace, cache_key: cacheKey, nbins }
    });
    
    return response.data;
  },

  /**
   * Gráficos comparativos
   */
  getComparativeCharts: async (marketplace, cacheKey) => {
    const response = await api.get('/dashboard/financial/comparative-public', {
      params: { marketplace, cache_key: cacheKey }
    });
    
    return response.data;
  },

  /**
   * Datos detallados con filtros
   */
  getDetailedData: async (marketplace, cacheKey, filters = {}) => {
    const params = {
      marketplace,
      cache_key: cacheKey,
      limit: filters.limit || 100,
      offset: filters.offset || 0
    };
    
    if (filters.proveedor_filter) params.proveedor_filter = filters.proveedor_filter;
    if (filters.min_utility !== undefined && filters.min_utility !== null && filters.min_utility !== '') {
      params.min_utility = filters.min_utility;
    }
    if (filters.max_utility !== undefined && filters.max_utility !== null && filters.max_utility !== '') {
      params.max_utility = filters.max_utility;
    }
    
    const response = await api.get('/dashboard/financial/detailed-data-public', { params });
    return response.data;
  },

  /**
   * Guardar en base de datos
   */
  saveToDatabase: async (marketplace, cacheKey) => {
    const formData = new FormData();
    formData.append('marketplace', marketplace);
    formData.append('cache_key', cacheKey);
    
    const response = await api.post('/dashboard/financial/save-to-db-public', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    
    return response.data;
  },

  /**
   * Limpiar caché
   */
  clearCache: async (marketplace) => {
    const response = await api.delete('/dashboard/financial/clear-cache-public', {
      params: { marketplace }
    });
    
    return response.data;
  },

  /**
   * Obtener URL para exportar CSV
   */
  getExportCsvUrl: (marketplace, cacheKey, filters = {}) => {
    const params = new URLSearchParams({
      marketplace,
      cache_key: cacheKey
    });
    
    if (filters.proveedor_filter) params.append('proveedor_filter', filters.proveedor_filter);
    if (filters.min_utility !== undefined && filters.min_utility !== null && filters.min_utility !== '') {
      params.append('min_utility', filters.min_utility.toString());
    }
    if (filters.max_utility !== undefined && filters.max_utility !== null && filters.max_utility !== '') {
      params.append('max_utility', filters.max_utility.toString());
    }
    
    return `${API_BASE_URL}/dashboard/financial/export-csv-public?${params.toString()}`;
  }
};

export default api; 